import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import AlertTemplate from './signage/templates/AlertTemplate';
import PromotionTemplate from './signage/templates/PromotionTemplate';
import EventTemplate from './signage/templates/EventTemplate';
import InfoTemplate from './signage/templates/InfoTemplate';
import WelcomeTemplate from './signage/templates/WelcomeTemplate';
import BlankTemplate from './signage/templates/BlankTemplate';

function SignageDisplay({ location }) {
  const [playlist, setPlaylist] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [config, setConfig] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [transitionClass, setTransitionClass] = useState('fade-in');
  const [videoPlayCount, setVideoPlayCount] = useState(0); // ✅ NEW: Force video remount
  const [lastRefreshCheck, setLastRefreshCheck] = useState(Date.now()); // ✅ Track refresh checks
  const [transitionType, setTransitionType] = useState('fade'); // ✅ Track current transition type
  
  const videoRef = useRef(null);
  const timerRef = useRef(null);

  const API_URL = import.meta.env.VITE_API_URL || window.location.origin;



  // ✅ FIX: Fetch playlist from API with cache-busting
  const fetchPlaylist = useCallback(async () => {
    console.log(`[SignageDisplay] 🔄 Fetching playlist for location: ${location}`);
    try {
      // ✅ CRITICAL FIX: Add timestamp to prevent 304 caching
      const timestamp = new Date().getTime();
      const response = await axios.get(
        `${API_URL}/api/signage/playlist/${location}?t=${timestamp}`,
        {
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        }
      );
      
      const { media, announcements, config: locationConfig, lastUpdated: serverLastUpdated } = response.data;

      console.log('[SignageDisplay] ✅ Playlist data received:', {
        mediaCount: media?.length || 0,
        announcementsCount: announcements?.length || 0,
        config: locationConfig,
        timestamp: new Date().toLocaleTimeString()
      });

      // Filter out hidden items for this location before building playlist
      const visibleMedia = (media || []).filter(
        item => !item.playlistOverrides?.[location]?.hidden
      );

      // Combine media and announcements into single playlist
      const combinedPlaylist = buildPlaylist(visibleMedia, announcements, locationConfig);
      
      setPlaylist(combinedPlaylist);
      setConfig(locationConfig);
      setLastUpdated(serverLastUpdated);
      
      // ✅ Set transition type from config
      const transition = locationConfig?.transitionEffect || 'fade';
      setTransitionType(transition);
      console.log(`[SignageDisplay] 🎬 Transition effect: ${transition}`);
      
      setLoading(false);
      setError(null);
      
      console.log(`[SignageDisplay] ✅ Playlist loaded: ${combinedPlaylist.length} items for ${location}`);
    } catch (err) {
      console.error('[SignageDisplay] ❌ Error fetching playlist:', err);
      setError('Failed to load playlist');
      setLoading(false);
    }
  }, [location, API_URL]);

  // Build playlist with announcements injected based on frequency
  const buildPlaylist = (media, announcements, locationConfig) => {
    console.log('[SignageDisplay] Building playlist:', {
      mediaCount: media?.length || 0,
      announcementsCount: announcements?.length || 0,
      exclusiveCount: announcements?.filter(a => a.displayMode === 'exclusive').length || 0,
      mixedCount: announcements?.filter(a => a.displayMode === 'mixed').length || 0
    });

    if (!media || media.length === 0) {
      console.log('[SignageDisplay] No media found, returning announcements only');
      return announcements || [];
    }

    if (!announcements || announcements.length === 0) {
      console.log('[SignageDisplay] No announcements found, returning media only');
      return media;
    }

    // Shuffle media if configured
    let orderedMedia = [...media];
    if (locationConfig?.shuffleMedia) {
      console.log('[SignageDisplay] Shuffling media');
      orderedMedia = shuffleArray(orderedMedia);
    }

    // Separate exclusive and mixed announcements
    const exclusiveAnnouncements = announcements.filter(a => a.displayMode === 'exclusive');
    const mixedAnnouncements = announcements.filter(a => a.displayMode === 'mixed');

    console.log('[SignageDisplay] Announcement breakdown:', {
      exclusive: exclusiveAnnouncements.length,
      mixed: mixedAnnouncements.length
    });

    // If there are exclusive announcements, they take priority (show only them)
    if (exclusiveAnnouncements.length > 0) {
      console.log('[SignageDisplay] ⚠️ Exclusive mode: Showing only announcements');
      return exclusiveAnnouncements.sort((a, b) => b.priority - a.priority);
    }

    // ✅ SMART INJECTION: Show ALL announcements AND ALL media
    const combined = [];
    
    if (mixedAnnouncements.length === 0) {
      return orderedMedia;
    }

    if (orderedMedia.length === 0) {
      // No media, just show all announcements
      console.log('[SignageDisplay] No media, showing all announcements');
      return mixedAnnouncements;
    }
    
    // Calculate optimal spacing between announcements
    const totalMedia = orderedMedia.length;
    const totalAnnouncements = mixedAnnouncements.length;
    
    // Calculate positions where announcements should be injected
    const spacing = Math.max(1, Math.floor(totalMedia / (totalAnnouncements + 1)));
    
    console.log(`[SignageDisplay] 🎯 Smart injection: ${totalAnnouncements} announcements across ${totalMedia} media, spacing: ${spacing}`);
    
    // Create a set of positions where announcements will be injected
    const announcementPositions = new Set();
    for (let i = 0; i < totalAnnouncements; i++) {
      const position = Math.min(spacing * (i + 1) + i, totalMedia + i);
      announcementPositions.add(position);
      console.log(`[SignageDisplay] 📢 Will inject announcement ${i + 1} at position ${position}`);
    }
    
    let mediaIndex = 0;
    let announcementIndex = 0;
    
    // Build final playlist
    const maxItems = totalMedia + totalAnnouncements;
    for (let position = 0; position < maxItems; position++) {
      if (announcementPositions.has(position) && announcementIndex < totalAnnouncements) {
        // Inject announcement at this position
        combined.push(mixedAnnouncements[announcementIndex]);
        console.log(`[SignageDisplay] ✅ Injected announcement "${mixedAnnouncements[announcementIndex].title}" at position ${position + 1}`);
        announcementIndex++;
      } else if (mediaIndex < totalMedia) {
        // Add media
        combined.push(orderedMedia[mediaIndex]);
        mediaIndex++;
      }
    }
    
    // ✅ CRITICAL: Ensure ALL announcements are added (fallback)
    while (announcementIndex < totalAnnouncements) {
      combined.push(mixedAnnouncements[announcementIndex]);
      console.log(`[SignageDisplay] ✅ Added remaining announcement "${mixedAnnouncements[announcementIndex].title}"`);
      announcementIndex++;
    }

    console.log('[SignageDisplay] Final playlist built:', {
      totalItems: combined.length,
      mediaItems: mediaIndex,
      announcementsInjected: announcementIndex,
      allAnnouncementsShown: announcementIndex === totalAnnouncements ? '✅ YES' : '❌ NO'
    });

    return combined;
  };

  // Shuffle array helper
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // ✅ FIX: Check for updates with cache-busting (polling every 10 seconds)
  const checkForUpdates = useCallback(async () => {
    try {
      console.log('[SignageDisplay] 🔍 Checking for updates...');
      
      // ✅ CRITICAL FIX: Add timestamp to prevent 304 caching
      const timestamp = new Date().getTime();
      const response = await axios.get(
        `${API_URL}/api/signage/playlist/${location}?t=${timestamp}`,
        {
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        }
      );
      
      const serverLastUpdated = new Date(response.data.lastUpdated).getTime();
      const clientLastUpdated = new Date(lastUpdated).getTime();

      if (serverLastUpdated > clientLastUpdated) {
        console.log('[SignageDisplay] 🔄 Updates detected, refreshing playlist...', {
          serverTime: new Date(serverLastUpdated).toLocaleTimeString(),
          clientTime: new Date(clientLastUpdated).toLocaleTimeString()
        });
        fetchPlaylist();
      } else {
        console.log('[SignageDisplay] ✅ Playlist is up to date');
      }
    } catch (err) {
      console.error('[SignageDisplay] ⚠️ Error checking for updates:', err);
      // Continue with cached playlist if update check fails
    }
  }, [location, lastUpdated, fetchPlaylist, API_URL]);

  // Initial load
  useEffect(() => {
    console.log('[SignageDisplay] 🚀 Initial playlist load');
    fetchPlaylist();
  }, [fetchPlaylist]);

  // ✅ Polling for updates every 10 seconds
  useEffect(() => {
    if (!lastUpdated) {
      console.log('[SignageDisplay] ⏳ Waiting for initial load before starting polling');
      return;
    }

    console.log('[SignageDisplay] ⏰ Starting 10-second polling for updates');
    
    const pollInterval = setInterval(() => {
      console.log('[SignageDisplay] 🔄 Polling interval triggered');
      checkForUpdates();
    }, 10000); // 10 seconds

    return () => {
      console.log('[SignageDisplay] 🛑 Stopping polling interval');
      clearInterval(pollInterval);
    };
  }, [lastUpdated, checkForUpdates]);

  // ✅ NEW: Poll for TV refresh requests every 10 seconds
  useEffect(() => {
    if (!location) return;

    console.log('[SignageDisplay] 🔄 Starting refresh polling for location:', location);

    const checkForRefresh = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/api/signage/refresh-tv/${location}/check?lastCheck=${lastRefreshCheck}`
        );
        
        if (response.data.shouldRefresh) {
          console.log('[SignageDisplay] 🔄 REFRESH REQUESTED! Reloading page...');
          console.log('[SignageDisplay] Refresh timestamp:', response.data.refreshTimestamp);
          
          // Hard reload (clears cache)
          window.location.reload(true);
        }
      } catch (err) {
        console.error('[SignageDisplay] ⚠️ Error checking for refresh:', err);
      }
    };

    // Check immediately on mount
    checkForRefresh();

    // Then check every 10 seconds
    const refreshInterval = setInterval(checkForRefresh, 10000);

    return () => {
      console.log('[SignageDisplay] 🛑 Stopping refresh polling');
      clearInterval(refreshInterval);
    };
  }, [location, lastRefreshCheck, API_URL]);

  // Handle media rotation
  useEffect(() => {
    if (!playlist || playlist.length === 0) return;

    const currentItem = playlist[currentIndex];
    if (!currentItem) return;

    console.log(`[SignageDisplay] Current item ${currentIndex + 1}/${playlist.length}:`, {
      type: currentItem.type,
      fileName: currentItem.fileName || currentItem.title,
      fileType: currentItem.fileType,
      duration: currentItem.duration
    });

    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // ✅ Apply transition effect based on config
    if (transitionType !== 'none') {
      setTransitionClass(`${transitionType}-exit`);
      setTimeout(() => setTransitionClass(`${transitionType}-enter`), 50);
    }

    // Handle different content types
    if (currentItem.type === 'media') {
      if (currentItem.fileType === 'video') {
        // Video - will auto-advance on onEnded event
        console.log(`[SignageDisplay] 🎥 Playing video: ${currentItem.fileName}`);
      } else {
        // Image - advance after duration
        const duration = currentItem.duration || config?.defaultImageDuration || 10;
        console.log(`[SignageDisplay] 🖼️ Displaying image: ${currentItem.fileName} for ${duration}s`);
        
        timerRef.current = setTimeout(() => {
          advanceToNext();
        }, duration * 1000);
      }
    } else if (currentItem.type === 'announcement') {
      // Announcement - advance after duration
      const duration = currentItem.duration || 15;
      console.log(`[SignageDisplay] 📢 Showing announcement: ${currentItem.title} for ${duration}s`);
      
      timerRef.current = setTimeout(() => {
        advanceToNext();
      }, duration * 1000);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [currentIndex, playlist, config]);

  // Advance to next item
  const advanceToNext = () => {
    setCurrentIndex((prevIndex) => {
      const nextIndex = (prevIndex + 1) % playlist.length;
      console.log(`[SignageDisplay] ⏭️ Advancing from ${prevIndex} to ${nextIndex}`);
      
      // ✅ FIX: If staying at same index (single item), force video remount
      if (nextIndex === prevIndex && playlist.length === 1) {
        setVideoPlayCount(prev => prev + 1);
        console.log('[SignageDisplay] 🔄 Single item playlist - forcing remount');
      }
      
      return nextIndex;
    });
  };

  // Handle video end
  const handleVideoEnd = () => {
    console.log('[SignageDisplay] Video ended, advancing...');
    advanceToNext();
  };

  // Render announcement template
  const renderAnnouncement = (announcement) => {
    const templates = {
      blank: BlankTemplate,
      alert: AlertTemplate,
      promotion: PromotionTemplate,
      event: EventTemplate,
      info: InfoTemplate,
      welcome: WelcomeTemplate,
    };

    const TemplateComponent = templates[announcement.template] || BlankTemplate;
    
    console.log('[SignageDisplay] Rendering announcement template:', announcement.template);
    
    return (
      <TemplateComponent
        title={announcement.title}
        message={announcement.message}
        styling={announcement.styling}
        content={announcement.content}
      />
    );
  };

  // Render current item
  const renderCurrentItem = () => {
    if (!playlist || playlist.length === 0) {
      console.log('[SignageDisplay] ⚠️ No content available');
      return (
        <div className="flex items-center justify-center h-full text-white text-2xl">
          <div className="text-center">
            <p className="mb-4">No content available</p>
            <p className="text-sm opacity-75">Waiting for media or announcements...</p>
          </div>
        </div>
      );
    }

    const currentItem = playlist[currentIndex];
    if (!currentItem) return null;

    if (currentItem.type === 'media') {
      if (currentItem.fileType === 'video') {
        return (
          <video
            key={`video-${currentIndex}-${videoPlayCount}`} // ✅ FIX: Include playCount to force remount
            ref={videoRef}
            src={`${API_URL}${currentItem.filePath}`}
            autoPlay
            muted
            playsInline
            onEnded={handleVideoEnd}
            onError={(e) => {
              console.error('[SignageDisplay] ❌ Video error:', e);
              advanceToNext();
            }}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
            }}
          />
        );
      } else {
        return (
          <img
            src={`${API_URL}${currentItem.filePath}`}
            alt={currentItem.fileName}
            onLoad={() => {
              console.log('[SignageDisplay] ✅ Image loaded successfully:', currentItem.fileName);
            }}
            onError={(e) => {
              console.error('[SignageDisplay] ❌ Image error:', currentItem.fileName, e);
              advanceToNext();
            }}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
            }}
          />
        );
      }
    } else if (currentItem.type === 'announcement') {
      return renderAnnouncement(currentItem);
    }

    return null;
  };

  // Loading state
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>
          <div style={styles.spinner}></div>
          <p style={styles.loadingText}>Loading {location} display...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>
          <p style={styles.errorText}>⚠️ {error}</p>
          <p style={styles.errorSubtext}>Retrying in 10 seconds...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div 
        className={transitionClass}
        style={styles.content}
      >
        {renderCurrentItem()}
      </div>

      {/* Debug info removed for production */}

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          /* ========================================
             TRANSITION STYLES - Multiple Types
             ======================================== */

          /* FADE - Simple fade in/out */
          .fade-exit {
            animation: fadeOut 500ms ease-out forwards;
          }
          .fade-enter {
            animation: fadeIn 500ms ease-in forwards;
          }

          @keyframes fadeOut {
            from { opacity: 1; }
            to { opacity: 0; }
          }

          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }

          /* CROSSFADE - Overlapping fade (smooth) */
          .crossfade-exit {
            animation: crossfadeOut 600ms ease-in-out forwards;
          }
          .crossfade-enter {
            animation: crossfadeIn 600ms ease-in-out forwards;
          }

          @keyframes crossfadeOut {
            from { opacity: 1; }
            to { opacity: 0; }
          }

          @keyframes crossfadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }

          /* FADE FAST - Quick fade */
          .fade-fast-exit {
            animation: fadeOutFast 300ms ease-out forwards;
          }
          .fade-fast-enter {
            animation: fadeInFast 300ms ease-in forwards;
          }

          @keyframes fadeOutFast {
            from { opacity: 1; }
            to { opacity: 0; }
          }

          @keyframes fadeInFast {
            from { opacity: 0; }
            to { opacity: 1; }
          }

          /* FADE SLOW - Gentle fade */
          .fade-slow-exit {
            animation: fadeOutSlow 800ms ease-in-out forwards;
          }
          .fade-slow-enter {
            animation: fadeInSlow 800ms ease-in-out forwards;
          }

          @keyframes fadeOutSlow {
            from { opacity: 1; }
            to { opacity: 0; }
          }

          @keyframes fadeInSlow {
            from { opacity: 0; }
            to { opacity: 1; }
          }

          /* SLIDE LEFT - Slide out left, slide in from right */
          .slide-left-exit {
            animation: slideOutLeft 500ms ease-in-out forwards;
          }
          .slide-left-enter {
            animation: slideInRight 500ms ease-in-out forwards;
          }

          @keyframes slideOutLeft {
            from { transform: translateX(0); opacity: 1; }
            to { transform: translateX(-100%); opacity: 0; }
          }

          @keyframes slideInRight {
            from { transform: translateX(100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }

          /* SLIDE RIGHT - Slide out right, slide in from left */
          .slide-right-exit {
            animation: slideOutRight 500ms ease-in-out forwards;
          }
          .slide-right-enter {
            animation: slideInLeft 500ms ease-in-out forwards;
          }

          @keyframes slideOutRight {
            from { transform: translateX(0); opacity: 1; }
            to { transform: translateX(100%); opacity: 0; }
          }

          @keyframes slideInLeft {
            from { transform: translateX(-100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }

          /* SLIDE (default - same as slide-left) */
          .slide-exit {
            animation: slideOutLeft 500ms ease-in-out forwards;
          }
          .slide-enter {
            animation: slideInRight 500ms ease-in-out forwards;
          }

          /* ZOOM FADE - Zoom + fade combo */
          .zoom-fade-exit {
            animation: zoomFadeOut 500ms ease-in-out forwards;
          }
          .zoom-fade-enter {
            animation: zoomFadeIn 500ms ease-in-out forwards;
          }

          @keyframes zoomFadeOut {
            from { transform: scale(1); opacity: 1; }
            to { transform: scale(0.8); opacity: 0; }
          }

          @keyframes zoomFadeIn {
            from { transform: scale(1.2); opacity: 0; }
            to { transform: scale(1); opacity: 1; }
          }

          /* NONE - No transition */
          .none-exit,
          .none-enter {
            animation: none;
          }

          /* Default fallback */
          .fade-in {
            animation: fadeIn 500ms ease-in forwards;
          }
          .fade-out {
            animation: fadeOut 500ms ease-out forwards;
          }
        `}
      </style>
    </div>
  );
}

const styles = {
  container: {
    width: '100vw',
    height: '100vh',
    backgroundColor: '#000000',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
    position: 'relative',
  },
  content: {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loading: {
    textAlign: 'center',
    color: '#ffffff',
  },
  spinner: {
    width: '64px',
    height: '64px',
    border: '4px solid rgba(255, 255, 255, 0.3)',
    borderTop: '4px solid #ffffff',
    borderRadius: '50%',
    margin: '0 auto 20px',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    fontSize: '24px',
    fontFamily: 'Inter, system-ui, sans-serif',
  },
  error: {
    textAlign: 'center',
    color: '#ffffff',
  },
  errorText: {
    fontSize: '32px',
    marginBottom: '16px',
    fontFamily: 'Inter, system-ui, sans-serif',
  },
  errorSubtext: {
    fontSize: '20px',
    opacity: 0.7,
    fontFamily: 'Inter, system-ui, sans-serif',
  },
};

export default SignageDisplay;